import { ECountry } from './countries';
import { ERegion } from './region';

export interface IFile {
  _id?: string;
  url?: string;
  [key: string]: any;
}

export interface IFpUserInfo {
  identityDocFile?: IFile;
  socialSecurityFile?: IFile;
  degreeFile?: IFile;
  notesEquivalenceFile?: IFile;
}

export interface IOposUserInfo {
  autonomousCommunity?: string;
  hasMasterDegree?: boolean;
}

export interface IPceUserInfo {
  studyBranch?: string;
  degree?: string;
  university?: string;
  autonomousCommunity?: string;
  needVisa?: boolean;
  hasCertificateDegree?: boolean;
  hasNotesEquivalence?: boolean;
}

export interface ISubjectUserInfo {
  course: string;
  note: number;
}

export interface IUniUserInfo {
  studyBranch?: string;
  degree?: string;
  university?: string;
  autonomousCommunity?: string;
  exams?: string;
  subjects?: ISubjectUserInfo[];
}

export enum EOnboardingDiscoverySource {
  GOOGLE = 'GOOGLE',
  SOCIAL_MEDIA = 'SOCIAL_MEDIA',
  ADVERTISEMENT = 'ADVERTISEMENT',
  FRIEND = 'FRIEND',
  FLYER = 'FLYER'
}

export const ONBOARDING_DISCOVERY_SOURCES = Object.values(EOnboardingDiscoverySource);

export enum EOnboardingChosenReason {
  COMPATIBILITY_WITH_WORK_AND_FAMILY = 'COMPATIBILITY_WITH_WORK_AND_FAMILY',
  AFFORDABLE_PRICE = 'AFFORDABLE_PRICE',
  QUALITY_PLATFORM_AND_CONTENT = 'QUALITY_PLATFORM_AND_CONTENT',
  PERSONALIZED_SUPPORT_AND_ATTENTION = 'PERSONALIZED_SUPPORT_AND_ATTENTION',
  FLEXIBILITY_TO_STUDY_AT_MY_RHYTHM = 'FLEXIBILITY_TO_STUDY_AT_MY_RHYTHM',
  EASY_VISA_PROCESS = 'EASY_VISA_PROCESS',
  FINANCIAL_OPTIONS = 'FINANCIAL_OPTIONS'
}

export const ONBOARDING_CHOSEN_REASONS = Object.values(EOnboardingChosenReason);

export enum ELegacyOnboardingChosenReason {
  QUIZ = 'QUIZ',
  MICROLESSON = 'MICROLESSON',
  TEACHER_FOLLOW_UP = 'TEACHER_FOLLOW_UP',
  SUCCESS_GUARANTEE = 'SUCCESS_GUARANTEE',
  ATTENTION = 'ATTENTION',
  HELP_IN_EVERYTHING = 'HELP_IN_EVERYTHING',
  OTHER = 'OTHER'
}

export enum EOnboaringFormStep {
  COMPLETE_PROFILE = 'COMPLETE_PROFILE',
  COMPLETE_PROFILE_FP = 'COMPLETE_PROFILE_FP',
  YOU_HAVE_MASTER_DEGREE = 'YOU_HAVE_MASTER_DEGREE',
  DEFINE_YOUR_GOAL = 'DEFINE_YOUR_GOAL',
  DEFINE_YOUR_GOAL_FP = 'DEFINE_YOUR_GOAL_FP',
  DEFINE_YOUR_GOAL_OPOS = 'DEFINE_YOUR_GOAL_OPOS',
  YOUR_SITUATION = 'YOUR_SITUATION',
  PREPAIR_FOR_THE_CHALLENGE = 'PREPAIR_FOR_THE_CHALLENGE',
  SUBJECT_NOTES = 'SUBJECT_NOTES',
  STUDY_PLAN = 'STUDY_PLAN',
  CHOOSE_COURSES = 'CHOOSE_COURSES',
  DISCOVER_SOURCES = 'DISCOVER_SOURCES',
  CHOSEN_REASONS = 'CHOSEN_REASONS',
  FINAL_STEP = 'FINAL_STEP'
}

export interface OnboardingFormValues {
  name?: string;
  lastName?: string;
  phone?: string;
  legalId?: string;
  birthday?: Date;
  country?: ECountry;

  onBoarding?: {
    discoverySource?: EOnboardingDiscoverySource;
    chosenReason?: EOnboardingChosenReason;
  };

  userInfo?: {
    fpUserInfo?: Partial<IFpUserInfo>;
    oposUserInfo?: Partial<IOposUserInfo>;
    pceUserInfo?: Partial<IPceUserInfo>;
    uniUserInfo?: Partial<IUniUserInfo>;
    examGroups?: string[];
  };

  planification?: {
    endDate?: Date;
    restDates?: Date[];
    restWeekdays?: string[];
  };
}

export const FP_ONBOARDING_FORM_STEPS = [
  EOnboaringFormStep.COMPLETE_PROFILE_FP,
  EOnboaringFormStep.DEFINE_YOUR_GOAL_FP,
  EOnboaringFormStep.STUDY_PLAN,
  EOnboaringFormStep.DISCOVER_SOURCES,
  EOnboaringFormStep.CHOSEN_REASONS,
  EOnboaringFormStep.FINAL_STEP
];

export const SELECTIVITY_ONBOARDING_FORM_STEPS = [
  EOnboaringFormStep.COMPLETE_PROFILE,
  EOnboaringFormStep.DEFINE_YOUR_GOAL,
  EOnboaringFormStep.PREPAIR_FOR_THE_CHALLENGE,
  EOnboaringFormStep.SUBJECT_NOTES,
  EOnboaringFormStep.STUDY_PLAN,
  EOnboaringFormStep.CHOOSE_COURSES,
  EOnboaringFormStep.DISCOVER_SOURCES,
  EOnboaringFormStep.CHOSEN_REASONS,
  EOnboaringFormStep.FINAL_STEP
];

export const SELECTIVITY_ONBOARDING_FORM_STEPS_BY_REGION: Record<ERegion, EOnboaringFormStep[]> = {
  [ERegion.ES]: SELECTIVITY_ONBOARDING_FORM_STEPS,
  [ERegion.PT]: SELECTIVITY_ONBOARDING_FORM_STEPS.filter(
    step => step !== EOnboaringFormStep.PREPAIR_FOR_THE_CHALLENGE
  )
};

export const PCE_ONBOARDING_FORM_STEPS = [
  EOnboaringFormStep.COMPLETE_PROFILE,
  EOnboaringFormStep.DEFINE_YOUR_GOAL,
  EOnboaringFormStep.PREPAIR_FOR_THE_CHALLENGE,
  EOnboaringFormStep.YOUR_SITUATION,
  EOnboaringFormStep.STUDY_PLAN,
  EOnboaringFormStep.CHOOSE_COURSES,
  EOnboaringFormStep.DISCOVER_SOURCES,
  EOnboaringFormStep.CHOSEN_REASONS,
  EOnboaringFormStep.FINAL_STEP
];

export const OPOS_EDUCATION_ONBOARDING_FORM_STEPS = [
  EOnboaringFormStep.COMPLETE_PROFILE,
  EOnboaringFormStep.YOU_HAVE_MASTER_DEGREE,
  EOnboaringFormStep.DEFINE_YOUR_GOAL_OPOS,
  EOnboaringFormStep.STUDY_PLAN,
  EOnboaringFormStep.DISCOVER_SOURCES,
  EOnboaringFormStep.CHOSEN_REASONS,
  EOnboaringFormStep.FINAL_STEP
];

export const OPOS_ONBOARDING_FORM_STEPS = [
  EOnboaringFormStep.COMPLETE_PROFILE,
  EOnboaringFormStep.DEFINE_YOUR_GOAL_OPOS,
  EOnboaringFormStep.STUDY_PLAN,
  EOnboaringFormStep.DISCOVER_SOURCES,
  EOnboaringFormStep.CHOSEN_REASONS,
  EOnboaringFormStep.FINAL_STEP
];

export const ONBOARDING_ALL_FORM_STEPS = [
  EOnboaringFormStep.COMPLETE_PROFILE,
  EOnboaringFormStep.YOU_HAVE_MASTER_DEGREE,
  EOnboaringFormStep.DEFINE_YOUR_GOAL,
  EOnboaringFormStep.DEFINE_YOUR_GOAL_FP,
  EOnboaringFormStep.DEFINE_YOUR_GOAL_OPOS,
  EOnboaringFormStep.YOUR_SITUATION,
  EOnboaringFormStep.PREPAIR_FOR_THE_CHALLENGE,
  EOnboaringFormStep.SUBJECT_NOTES,
  EOnboaringFormStep.STUDY_PLAN,
  EOnboaringFormStep.CHOOSE_COURSES,
  EOnboaringFormStep.DISCOVER_SOURCES,
  EOnboaringFormStep.CHOSEN_REASONS,
  EOnboaringFormStep.FINAL_STEP
];
